import { Button, cn } from "@nexus/ui";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { OrderLineVideoLabelEditor } from "@/features/marketplace/components/OrderLineVideoLabelEditor";
import { ScriptReviewAction } from "@/features/marketplace/components/ScriptReviewAction";
import { StatusPill } from "@/features/marketplace/components/orders-list/StatusMenu";
import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import { orderLineVideoFulfillmentStatus } from "@/features/marketplace/lib/video-display-status";
import { orderLineVideoDisplayName } from "@/features/marketplace/lib/order-line-videos";
import {
  canChangePartnerVideos,
  canPartnerManageScriptVideos,
  partnerVideoSlotEmpty,
} from "@/features/marketplace/partner/lib/partner-videos";
import {
  PARTNER_QUEUE_CELL_DUE_CLASS,
  PARTNER_QUEUE_CELL_RECEIVED_CLASS,
  PARTNER_QUEUE_CELL_SCRIPTS_CLASS,
  PARTNER_QUEUE_CELL_STATUS_CLASS,
  PARTNER_QUEUE_CELL_TYPE_CLASS,
  PARTNER_QUEUE_GRID_CLASS,
  PARTNER_QUEUE_ROW_PADDING_CLASS,
} from "@/features/marketplace/partner/lib/partner-queue-grid";
import type { OrderLineVideo } from "@/features/marketplace/types/marketplace.types";
import { useUpdateOrderScriptVideoLabel } from "@/lib/queries/marketplace.queries";

interface PartnerVideoRowProps {
  orderId: string;
  orderScriptId: string;
  scriptTitle: string;
  fulfillmentStatus: string;
  video: OrderLineVideo;
  videos: readonly OrderLineVideo[];
  rowIndex: number;
  selected: boolean;
  /** The Status filter kept this video because its status matched. */
  matchesStatusFilter?: boolean;
  onSelect: () => void;
}

export function PartnerVideoRow({
  orderId,
  orderScriptId,
  scriptTitle,
  fulfillmentStatus,
  video,
  videos,
  rowIndex,
  selected,
  matchesStatusFilter = false,
  onSelect,
}: PartnerVideoRowProps) {
  const { t } = useTranslation();
  const updateLabel = useUpdateOrderScriptVideoLabel();
  const label = orderLineVideoDisplayName(video, t);
  const scriptStatus = fulfillmentStatus as MarketplaceFulfillmentStatus;
  const videoFulfillmentStatus =
    video.fulfillmentStatus ??
    orderLineVideoFulfillmentStatus(scriptStatus, video, { videos });
  const canManageVideos = canPartnerManageScriptVideos({
    fulfillmentStatus: scriptStatus,
    videos,
  });
  const videoNeedsRevision =
    video.reviewStatus === "REVISION_REQUESTED" ||
    videoFulfillmentStatus === "SENT_FOR_REVISION";
  const lineOpenForAllVideos = canChangePartnerVideos(scriptStatus);
  const editable = canManageVideos && video.id !== null;
  const canSelect =
    canManageVideos &&
    (lineOpenForAllVideos ||
      videoNeedsRevision ||
      partnerVideoSlotEmpty(video));

  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.target !== event.currentTarget || !canSelect) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <li
      id={`partner-video-${orderScriptId}-${video.id ?? video.position}`}
      role="row"
      aria-level={3}
      aria-rowindex={rowIndex}
      aria-selected={canSelect ? selected : undefined}
      data-partner-video-row
      data-tree-row
      tabIndex={canSelect ? 0 : undefined}
      onClick={canSelect ? onSelect : undefined}
      onKeyDown={handleKeyDown}
      className={cn(
        PARTNER_QUEUE_GRID_CLASS,
        PARTNER_QUEUE_ROW_PADDING_CLASS,
        "relative min-h-11 scroll-mt-20 py-1.5 outline-none transition-colors",
        canSelect && "cursor-pointer hover:bg-muted/25 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50",
        selected || matchesStatusFilter ? "bg-primary-subtle/60" : "bg-muted/10",
      )}
    >
      <span aria-hidden role="gridcell" />
      <span
        aria-hidden
        className="absolute bottom-0 left-5 top-0 w-px bg-border"
      />
      <span
        aria-hidden
        className="absolute left-5 top-1/2 h-px w-4 bg-border"
      />
      <span
        aria-hidden
        className="absolute bottom-0 left-10 top-0 w-px bg-border"
      />
      <span
        aria-hidden
        className="absolute left-10 top-1/2 h-px w-3 bg-border"
      />

      <div role="gridcell" className="min-w-0 pl-14">
        <OrderLineVideoLabelEditor
          video={video}
          editable={editable}
          isPending={updateLabel.isPending}
          onSave={(nextLabel) => {
            if (!video.id) return;
            updateLabel.mutate({
              orderId,
              orderScriptId,
              deliverableId: video.id,
              label: nextLabel,
            });
          }}
        />
        {video.revisionNote?.trim() ? (
          <p className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-warning-subtle-foreground">
            {video.revisionNote}
          </p>
        ) : null}
      </div>

      <span role="gridcell" aria-hidden className={PARTNER_QUEUE_CELL_TYPE_CLASS} />
      <span
        role="gridcell"
        aria-hidden
        className={cn(
          PARTNER_QUEUE_CELL_SCRIPTS_CLASS,
          "text-xs text-muted-foreground/45",
        )}
      >
        —
      </span>

      <span role="gridcell" className={PARTNER_QUEUE_CELL_STATUS_CLASS}>
        {videoFulfillmentStatus ? (
          <StatusPill status={videoFulfillmentStatus} viewer="partner" />
        ) : null}
      </span>

      <span role="gridcell" aria-hidden className={PARTNER_QUEUE_CELL_RECEIVED_CLASS} />
      <span role="gridcell" aria-hidden className={PARTNER_QUEUE_CELL_DUE_CLASS} />

      <span role="gridcell" className="flex items-center justify-end gap-1">
        {video.scriptReviewId || video.preflightUrl ? (
          <span onClick={(event) => event.stopPropagation()}>
            <ScriptReviewAction
              scriptReviewId={video.scriptReviewId}
              preflightUrl={video.preflightUrl}
              label={t("marketplace.partner.actions.view", "View")}
              ariaLabel={t("marketplace.partner.videos.viewAria", {
                label,
                title: scriptTitle,
                defaultValue: "View review for {{label}} on {{title}}",
              })}
              variant="outline"
              size="sm"
              compact
            />
          </span>
        ) : null}
        {canSelect ? (
          <Button
            type="button"
            size="sm"
            variant={selected ? "default" : "outline"}
            aria-pressed={selected}
            onClick={(event) => {
              event.stopPropagation();
              onSelect();
            }}
          >
            {t("marketplace.partner.videos.replace", "Replace")}
          </Button>
        ) : null}
      </span>
    </li>
  );
}
